import { ActionButton, CopyIconButton, useCopyFeedback } from '@augmentt-dev/ui-components'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import styles from './CopyButton.module.scss'

const COPY_ICON = 'far fa-copy'

const propTypes = {
  /**
   * Optional visible label. With a label the copy renders as an icon+label ActionButton; without
   * one it is a bare ghost icon (e.g. inline next to a value, or revealed on a table row's hover).
   */
  label: PropTypes.node,
  /**
   * Text to be copied
   */
  textToCopy: PropTypes.string.isRequired,
  /**
   * Class to override default styles
   */
  className: PropTypes.string,
  /**
   * Id set on the button element
   */
  id: PropTypes.string,
}

/**
 * Copy affordance. Delegates to the design-system `CopyIconButton` for the icon-only case, and renders
 * a labelled `ActionButton` otherwise; both share the same clipboard write + "Copied" toast via
 * `useCopyFeedback`, so every copy control in the app behaves identically.
 */
function CopyButton({ label = null, textToCopy, className = '', id = undefined }) {
  const { copy, toast } = useCopyFeedback()
  const handleCopy = useCallback((event) => copy(textToCopy, event), [copy, textToCopy])

  if (!label) {
    return (
      <span className={clsx(styles.wrapper, className)}>
        <CopyIconButton id={id} value={textToCopy} />
      </span>
    )
  }

  return (
    <span className={clsx(styles.wrapper, className)}>
      <ActionButton id={id} icon={COPY_ICON} label={label} onClick={handleCopy} />
      {toast}
    </span>
  )
}

CopyButton.propTypes = propTypes

export default CopyButton
